import { Icon } from '../components/Icon';
import { quoteDate } from '../lib/format';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { step } from '../telemetry';
import type { Folder, MailCategory, MessageDetail, MessageSummary, SpecialUse, User } from '../types';
import { AppRail, type Section } from '../components/AppRail';
import { FolderList, folderLabel } from '../components/FolderList';
import { MessageList } from '../components/MessageList';
import { CategoryTabs } from '../components/CategoryTabs';
import { MessageView } from '../components/MessageView';
import { Compose, type ComposeDraft } from '../components/Compose';
import { VerifyBanner } from '../components/VerifyBanner';
import { useDialog } from '../components/Dialog';
import { Admin } from './Admin';
import { Settings } from './Settings';
import { Migration } from './Migration';
import { Billing } from './Billing';
import { AccountBanner } from '../components/AccountBanner';

interface Props {
  user: User;
  onLogout: () => void;
  /** Profil dəyişəndə App istifadəçini yenidən yükləyir. */
  onProfileChanged: () => void;
}

/**
 * Açıq məktubdan cavab qaralaması qurur.
 *
 * `In-Reply-To` və `References` zənciri bağlayır — onlarsız cavab poçt
 * proqramlarında ayrı söhbət kimi görünür. `References` köhnə zəncirin
 * üstünə ƏLAVƏ olunur, əvəz etmir.
 */
function replyTo(m: MessageDetail): ComposeDraft {
  const addr = m.from?.address ?? '';
  const name = m.from?.name ?? '';
  const quoted = (m.text ?? '')
    .split('\n')
    .slice(0, 60)
    .map((l) => `> ${l}`)
    .join('\n');

  const chain = [...m.references, ...(m.messageId ? [m.messageId] : [])];
  const refs = chain.length > 20 ? [chain[0]!, ...chain.slice(-19)] : chain;

  return {
    to: name ? `${name} <${addr}>` : addr,
    // ⚠️ BOŞ MÖVZUDA «Re: » (sonu boşluq) ALINMIR.
    // Arxa tərəf artıq yer tutucu qaytarmır, ona görə boş halı burada
    // idarə etmək lazımdır — əks halda alıcıya sonunda boşluq olan
    // «Re: » mövzusu gedərdi.
    subject: m.subject ? (/^re:/i.test(m.subject) ? m.subject : `Re: ${m.subject}`) : 'Re:',
    text: `\n\n${quoteDate(m.date)} tarixində ${addr} yazdı:\n${quoted}`,
    // ⚠️ `threadId` DEYİL — `messageId`.
    //
    // Əvvəl burada `m.threadId` yazılırdı. O dəyər IMAP-ın `OBJECTID`
    // genişlənməsindən gəlir və DOVECOT ONU ELAN ETMİR, ona görə həmişə
    // `null` olurdu. Şərti spread boşa düşür və göndərdiyimiz hər cavab
    // `In-Reply-To` / `References` BAŞLIQLARI OLMADAN gedirdi — alıcının
    // Gmail-ində hər cavab AYRI SÖHBƏT kimi görünürdü. Sınaqla təsdiqləndi:
    // `Sent` qovluğunda bir dənə də `In-Reply-To` başlığı yox idi.
    //
    // RFC 5322 §3.6.4: `In-Reply-To` valideynin `Message-ID`-si olmalıdır;
    // `References` isə valideynin zənciri + valideynin özüdür.
    //
    // Zəncir 20 həlqədə kəsilir — uzun söhbətlərdə başlıq bəzi serverlərin
    // həddini keçir. Kök həlqə saxlanılır, çünki söhbətin başlanğıcını
    // itirsək qruplaşdırma pozulur.
    ...(m.messageId ? { inReplyTo: m.messageId } : {}),
    ...(refs.length ? { references: refs } : {}),
  };
}

/** Ünvanı `Ad <ünvan>` şəklində yazır — boş adda yalnız ünvan. */
function fmtAddr(a: { name: string; address: string }): string {
  return a.name ? `${a.name} <${a.address}>` : a.address;
}

/**
 * «Hamısına cavab» qaralaması.
 *
 * ⚠️ ÖZ ÜNVANIMIZ ÇIXARILIR. Onsuz istifadəçi hər cavabda özünə də nüsxə
 * göndərir və qutusu öz məktubları ilə dolur. Müqayisə hərf registrindən
 * asılı olmadan aparılır — `Info@` və `info@` eyni qutudur.
 *
 * ⚠️ TƏKRARLAR SİLİNİR. Eyni adam həm `To`, həm `Cc` sahəsində ola bilər;
 * ikisini birləşdirəndə ünvan iki dəfə düşərdi.
 */
function replyAllTo(m: MessageDetail, self: string): ComposeDraft {
  const base = replyTo(m);
  const mine = self.toLowerCase();
  const seen = new Set<string>([mine, (m.from?.address ?? '').toLowerCase()]);
  const others: string[] = [];

  for (const a of [...m.to, ...m.cc]) {
    const key = a.address.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    others.push(fmtAddr(a));
  }
  return others.length > 0 ? { ...base, cc: others.join(', ') } : base;
}

/**
 * «Yönləndir» qaralaması.
 *
 * ⚠️ QOŞMALAR İNDİ DAŞINIR — VƏ SERVER TƏRƏFDƏ.
 * Əvvəl daşınmırdı: mətnin sonunda «bu qoşmalar getmədi» xəbərdarlığı
 * yazılırdı və istifadəçi faylları əl ilə endirib yenidən əlavə etməli
 * olurdu. İndi yalnız mənbənin qovluğu və UID-i göndərilir; faylları
 * IMAP-dan server özü götürür. Brauzer 20 MB-ı endirib yenidən
 * yükləmir — eyni baytlar şəbəkədən iki dəfə keçməməlidir.
 *
 * ⚠️ `In-Reply-To` QOYULMUR. Yönləndirmə cavab deyil — yeni söhbətdir.
 * Zəncir başlığını qoysaq, məktub alıcının poçtunda tamam ayrı bir
 * yazışmanın içinə düşərdi.
 */
function forwardOf(m: MessageDetail, path: string): ComposeDraft {
  const head = [
    '',
    '',
    '---------- Yönləndirilən məktub ----------',
    `Kimdən: ${m.from ? fmtAddr(m.from) : '(naməlum)'}`,
    `Tarix: ${quoteDate(m.date)}`,
    `Mövzu: ${m.subject}`,
    `Kimə: ${m.to.map(fmtAddr).join(', ') || '—'}`,
    ...(m.cc.length ? [`Kopya: ${m.cc.map(fmtAddr).join(', ')}`] : []),
    '',
    '',
  ].join('\n');

  return {
    to: '',
    subject: m.subject ? (/^fwd:/i.test(m.subject) ? m.subject : `Fwd: ${m.subject}`) : 'Fwd:',
    text: head + (m.text ?? ''),
    // Qoşma varsa mənbəni göstəririk; yoxdursa serveri boş yerə
    // IMAP-a getməyə məcbur etmirik.
    ...(m.attachments.length > 0
      ? {
          forwardOf: { path, uid: m.uid },
          forwardAttachments: m.attachments.map((a) => ({
            filename: a.filename,
            size: a.size,
          })),
        }
      : {}),
  };
}

export function Mail({ user, onLogout, onProfileChanged }: Props): React.ReactElement {
  const [section, setSection] = useState<Section>('mail');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [path, setPath] = useState('INBOX');
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [open, setOpen] = useState<MessageDetail | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [loadingList, setLoadingList] = useState(true);
  /** Siyahı ümumiyyətlə yüklənmədi — ekranın ortasında xəta göstərilir. */
  const [listError, setListError] = useState(false);
  /** Qovluqdakı ÜMUMİ məktub sayı — yüklənmişdən fərqli. */
  const [total, setTotal] = useState(0);

  /*
    NİŞANLAR — Gələnlər qutusunun kateqoriya süzgəci.

    ⚠️ SEÇİM `localStorage`-DƏ SAXLANILIR, BAZADA YOX.
    Bu, GÖRÜNÜŞ tərcihidir: hansı nişanda dayandığın və nişanların
    ümumiyyətlə göstərilib-göstərilməməsi. Bazaya yazsaq, miqrasiya,
    API ucu və sinxronlaşdırma lazım gələrdi — bir düymənin vəziyyəti
    üçün bahalı qiymət. Cihazlar arasında sinxron olmaması isə zərər
    vermir: adam noutbukunda nişanları söndürübsə, telefonda onları
    yenidən görməsi problem deyil.
  */
  const [category, setCategory] = useState<MailCategory | null>(() => {
    const v = localStorage.getItem('mailora.category');
    return v === 'primary' || v === 'social' || v === 'promotions' || v === 'updates' ? v : null;
  });
  const [tabsOn, setTabsOn] = useState(() => localStorage.getItem('mailora.tabs') !== 'off');
  const [counts, setCounts] = useState<Record<MailCategory, number> | null>(null);
  const [scanned, setScanned] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Uğur bildirişi XƏTADAN AYRI saxlanılır.
   *
   * ⚠️ `loadMessages` uğurlu yüklənmədə `setError(null)` edir — siyahı
   * yeniləndikdə köhnə xəta qalmasın deyə. Təsdiqi də `error`-a yazsaq,
   * göndərmədən sonrakı `refresh()` onu dərhal silir və istifadəçi heç nə
   * görmür. Məktub gedir, amma «getdi» demirik.
   */
  const [notice, setNotice] = useState<string | null>(null);
  // ⚠️ `window.confirm` əvəzi — bax components/Dialog.tsx
  const dlg = useDialog();
  /** `null` — pəncərə bağlıdır. Obyekt olanda açıqdır və ilkin dəyərləri daşıyır. */
  const [draft, setDraft] = useState<ComposeDraft | null>(null);
  /** Xarici göndəriş bloklanıbsa istifadəçi bunu BİLMƏLİDİR. */
  const [outboundBlocked, setOutboundBlocked] = useState(false);
  /** Boş sətir — axtarış rejimi söndürülüb, adi siyahı göstərilir. */
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchInfo, setSearchInfo] = useState<string | null>(null);
  /**
   * Mobil qovluq siyirməsi.
   *
   * ⚠️ MOBİLDƏ QOVLUQ SÜTUNU `display:none` İDİ VƏ ƏVƏZİ YOX İDİ.
   * Telefonda istifadəçi seçilmiş qovluqda kilidlənirdi — Göndərilənlərə,
   * Qaralamalara, Səbətə keçmək ÜMUMİYYƏTLƏ mümkün deyildi. Bu, zəif
   * uyğunlaşma deyil, sınıq naviqasiyadır.
   */
  const [drawer, setDrawer] = useState(false);
  /** Yalnız oxunmamışları göstər — brauzerdə süzülür, serverə sorğu yox. */
  const [unreadOnly, setUnreadOnly] = useState(false);

  /**
   * GERİ QAYTARMA.
   *
   * ⚠️ BU FUNKSİYA REAL İTKİDƏN SONRA YAZILIB. 2026-08-06-da bir sınaq
   * skripti «hamısını seç» edib silmə düyməsinə basdı və 20 məktub getdi;
   * təsdiq pəncərəsi vardı, amma səhv artıq edilmişdi və geri yol yox idi.
   *
   * Köçürmə (arxiv, spam, səbət) GERİ QAYTARILA BİLƏR — mesaj başqa
   * qovluqdadır, itməyib. Ona görə hər köçürmədən sonra 12 saniyəlik
   * pəncərə açılır.
   *
   * ⚠️ HƏMİŞƏLİK SİLMƏ GERİ QAYTARILMIR və qaytarılmamalıdır — mesaj
   * serverdən yox olub. Orada yeganə müdafiə təsdiq sualıdır.
   */
  const [undo, setUndo] = useState<{ from: string; to: string; uids: number[]; label: string } | null>(null);

  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), 12_000);
    return () => clearTimeout(t);
  }, [undo]);

  /** Yaddaş istifadəsi — qovluq siyahısının altında göstərilir. */
  const [quota, setQuota] = useState<{ usedBytes: number; limitBytes: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .systemStatus()
      .then((s) => {
        if (!cancelled) setOutboundBlocked(s.outbound.blocked);
      })
      .catch(() => {
        // Vəziyyət alınmasa xəbərdarlıq göstərmirik — yalan xəbərdarlıq
        // heç olmamasından pisdir.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Cari qovluğun sistem növü — Səbətdə davranış fərqlidir. */
  const currentUse = folders.find((f) => f.path === path)?.specialUse ?? null;
  /** Səbət və Spamda «Sil» KÖÇÜRMƏ deyil, həmişəlik silmədir. */
  const isPurgeFolder = currentUse === 'trash' || currentUse === 'junk';

  /**
   * Tab başlığında oxunmamış sayı.
   *
   * ⚠️ Panel arxa planda qalanda YEGANƏ siqnaldır. Poçt proqramında bu,
   * gözlənilən davranışdır — istifadəçi tabı görmədən yeni məktubu bilir.
   */
  useEffect(() => {
    const unread = folders.find((f) => f.specialUse === 'inbox')?.unseen ?? 0;
    document.title = unread > 0 ? `(${unread}) Mailora` : 'Mailora';
    return () => {
      document.title = 'Mailora';
    };
  }, [folders]);

  const findPath = useCallback(
    (use: SpecialUse): string | null => folders.find((f) => f.specialUse === use)?.path ?? null,
    [folders],
  );

  const loadFolders = useCallback(async () => {
    try {
      const { folders: f } = await api.folders();
      setFolders(f);
    } catch {
      setError('Qovluqlar yüklənmədi.');
    }
  }, []);

  const PAGE = 50;

  const loadMessages = useCallback(
    async (target: string, showSpinner = true, cat: MailCategory | null = category) => {
    if (showSpinner) setLoadingList(true);
    try {
      /*
        ⚠️ NİŞAN YALNIZ GƏLƏNLƏR QUTUSUNDA ÖTÜRÜLÜR.
        Arxa tərəf onsuz da INBOX-dan kənarda süzgəci saymır, amma
        burada da göndərməmək daha düzgündür: «Göndərilənlər»ə keçəndə
        sorğu ünvanında qalan `category=promotions` sətri gələcəkdə
        kimisə yanlış istiqamətə aparardı.
      */
      const useCat = tabsOn && target.toUpperCase() === 'INBOX' ? cat : null;
      const r = await api.messages(target, 0, PAGE, useCat);
      setMessages(r.messages);
      setTotal(r.total);
      setCounts(r.counts);
      setScanned(r.scanned);
      setError(null);
      setListError(false);
    } catch {
      // ⚠️ SİYAHI XƏTASI AYRICA SAXLANILIR.
      // Ümumi `error` zolağı yuxarıda kiçik sətirdir; siyahı ümumiyyətlə
      // yüklənməyibsə ekranın ortası BOŞ qalırdı və istifadəçi «poçtum
      // yoxdur» sanırdı. İndi orada nə baş verdiyi və təkrar cəhd düyməsi
      // göstərilir.
      setListError(true);
      setError('Məktublar yüklənmədi.');
    } finally {
      setLoadingList(false);
    }
  },
  [category, tabsOn],
  );

  const runUndo = useCallback(async () => {
    if (!undo) return;
    const u = undo;
    setUndo(null);
    try {
      // Geri qaytarma = əks istiqamətdə köçürmə.
      await api.move(u.to, u.from, u.uids);
      setNotice(`${u.uids.length} məktub geri qaytarıldı.`);
      void loadMessages(path, false);
      void loadFolders();
    } catch {
      setError('Geri qaytarmaq alınmadı — məktublar hədəf qovluqdadır.');
    }
  }, [undo, path, loadMessages, loadFolders]);

  /**
   * Növbəti səhifəni siyahının SONUNA əlavə edir.
   *
   * ⚠️ BU OLMADAN QOVLUĞUN YALNIZ SON 50 MƏKTUBU ƏLÇATANDI.
   *
   * Backend `offset`/`limit` onsuz da dəstəkləyirdi (service.ts, limit 200-ə
   * qədər), amma panel `offset`-i heç vaxt artırmırdı. 300 məktublu qutuda
   * istifadəçi yalnız son 50-ni görürdü, qalanı isə YALNIZ axtarışla — yəni
   * nə axtaracağını əvvəlcədən bilirsə. Müştəri bunu «məktublarım itib»
   * kimi başa düşür.
   *
   * ⚠️ `offset` MESAJ SAYINA GÖRƏDİR, səhifə nömrəsinə görə yox. Yeni məktub
   * gələndə sıra sürüşür və səhifə nömrəsi ilə işləsək, sərhəddəki məktub
   * ya iki dəfə görünər, ya heç görünməz.
   */
  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const useCat = tabsOn && path.toUpperCase() === 'INBOX' ? category : null;
      const r = await api.messages(path, messages.length, PAGE, useCat);
      const more = r.messages;
      setTotal(r.total);
      setCounts(r.counts);
      setScanned(r.scanned);
      // Təkrarları kəsirik — aralıqda gələn məktub sıranı sürüşdürə bilər.
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.uid));
        return [...prev, ...more.filter((m) => !seen.has(m.uid))];
      });
    } catch {
      setError('Növbəti məktublar yüklənmədi.');
    } finally {
      setLoadingMore(false);
    }
  }, [path, messages.length, loadingMore, category, tabsOn]);

  useEffect(() => {
    void loadFolders();
    // Kvota nadir dəyişir — yalnız açılışda və göndərmədən sonra oxunur.
    void api.quota().then((r) => setQuota(r.quota)).catch(() => undefined);
  }, [loadFolders]);

  /**
   * CANLI YENİLƏNMƏ.
   *
   * ⚠️ BU OLMADAN PANEL «ÖLÜ» GÖRÜNÜR. Əvvəl heç bir avtomatik yenilənmə
   * yox idi: istifadəçi telefonuna bildiriş alırdı, panelə baxırdı və orada
   * heç nə görmürdü — yeni məktub yalnız ↻ düyməsinə basanda gəlirdi.
   * Poçt proqramında bu, ən çox hiss edilən nöqsandır.
   *
   * İKİ TETİKLƏYİCİ, İKİ FƏRQLİ SƏBƏB:
   *
   * 1. `visibilitychange` — istifadəçi başqa tabdan qayıdanda DƏRHAL.
   *    İnsan məhz o an poçtuna baxır; 30 saniyə gözlətmək mənasızdır.
   *
   * 2. 30 saniyəlik interval — tab açıq qalanda.
   *    ⚠️ Gizli tabda İŞLƏMİR. Onlarla açıq tab varsa, hər biri hər
   *    30 saniyədə IMAP bağlantısı istəyər və server boş yerə yüklənər.
   *
   * Bu, IDLE deyil — ideal həll `imapPool`-un onsuz da yaydığı `newMail`
   * hadisəsini WebSocket ilə ötürməkdir (hazırda o hadisəni HEÇ KİM
   * dinləmir). Yenilənmə ondan qat-qat ucuzdur və indi lazımdır.
   */
  useEffect(() => {
    // Səssiz yenilənmə — spinner göstərmirik, yoxsa siyahı hər yarım
    // dəqiqədə gözün qarşısında sayrışar.
    const refresh = (): void => {
      if (document.hidden) return;
      // ⚠️ AXTARIŞ REJİMİNDƏ YENİLƏMİRİK. `loadMessages` qovluğun adi
      // siyahısını gətirir və axtarış nəticələrini SİLƏRDİ — istifadəçi
      // tapdığı məktuba baxarkən siyahı altından qaçardı.
      if (query.trim().length >= 2) return;
      void loadMessages(path, false);
      void loadFolders();
    };

    const onVisible = (): void => {
      if (!document.hidden) refresh();
    };

    const timer = setInterval(refresh, 30_000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [path, query, loadMessages, loadFolders]);


  useEffect(() => {
    setOpen(null);
    setChecked(new Set());
    setDrawer(false);
    void loadMessages(path);
  }, [path, loadMessages]);

  // Escape ilə bağlanma — siyirmə açıq qalıb istifadəçini kilidləməsin.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawer(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  /**
   * Serverdə axtarır.
   *
   * ⚠️ Brauzerdə filtrləmək SƏHV olardı: panelə yalnız son 50 mesaj yüklənir,
   * yəni istifadəçi köhnə məktubu tapa bilməzdi və «tapılmadı» yalan olardı.
   * IMAP SEARCH bütün qovluğu serverdə süzür.
   */
  const runSearch = useCallback(
    async (q: string) => {
      if (q.trim().length < 2) {
        setSearchInfo(null);
        await loadMessages(path);
        return;
      }
      setSearching(true);
      setError(null);
      try {
        step(`Axtarış: ${q}`);
        const r = await api.search(path, q.trim());
        setMessages(r.messages);
        setSearchInfo(
          r.total === 0
            ? 'Nəticə yoxdur'
            : `${r.total} nəticə${r.truncated ? ` (ilk ${r.messages.length} göstərilir)` : ''}`,
        );
      } catch {
        setError('Axtarış alınmadı.');
      } finally {
        setSearching(false);
      }
    },
    [path, loadMessages],
  );

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadFolders(), loadMessages(path, false)]);
    setRefreshing(false);
  }, [loadFolders, loadMessages, path]);

  const openMessage = useCallback(
    async (uid: number) => {
      try {
        const { message } = await api.message(path, uid);

        // ⚠️ QARALAMA OXUNMUR, REDAKTƏ EDİLİR.
        // Əvvəl qaralamaya klikləyəndə oxucu açılırdı — yəni istifadəçi öz
        // yarımçıq məktubunu YALNIZ BAXA bilirdi, davam etdirə bilmirdi.
        // Qaralamanın bütün mənası davam etdirməkdir; bu, sınıq axın idi.
        //
        // `draft` bayrağı yalnız detal obyektində gəlir (siyahı sətrində
        // də var, amma burada onsuz da tam məktub əlimizdədir).
        if (message.draft) {
          setDraft({
            to: message.to.map((a) => a.address).join(', '),
            ...(message.cc.length ? { cc: message.cc.map((a) => a.address).join(', ') } : {}),
            subject: message.subject,
            text: message.text ?? '',
            replaceUid: uid,
            // ⚠️ `inReplyTo` QOYULMUR. Qaralamanın öz `Message-ID`-si
            // cavab zənciri deyil — onu buraya versək, məktub göndəriləndə
            // özünə cavab kimi görünərdi.
          });
          return;
        }

        setOpen(message);
        // Server mesajı \Seen işarələyir — siyahını da dərhal uyğunlaşdırırıq
        // ki, istifadəçi yenilənməni gözləməsin.
        setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: true } : m)));
        void loadFolders();
      } catch {
        setError('Məktub açılmadı.');
      }
    },
    [path, loadFolders],
  );

  /**
   * Açıq məktubu köçürür.
   *
   * ⚠️ `SpecialUse` DEYİL, YOL QƏBUL EDİR.
   * Əvvəl yalnız sistem qovluqlarına köçürmək olardı — istifadəçinin öz
   * qovluğuna köçürmək üçün heç bir yol yox idi, halbuki arxa tərəf
   * (`/api/mail/move`) istənilən yolu onsuz da qəbul edir. Yəni imkan
   * vardı, sadəcə interfeys onu açmırdı.
   */
  const moveOpen = useCallback(
    async (dest: SpecialUse | { path: string; label: string }) => {
      if (!open) return;
      const uid = open.uid;
      const use = typeof dest === 'string' ? dest : null;

      // Səbətdə/Spamda «Sil» — həmişəlik (bax: bulkMove-dakı izahat).
      if (use === 'trash' && isPurgeFolder) {
        const ok = await dlg.ask({
          title: 'Məktub həmişəlik silinsin?',
          body: 'Geri qaytarmaq mümkün olmayacaq — səbətə düşmür, birbaşa silinir.',
          confirmLabel: 'Həmişəlik sil',
          danger: true,
        });
        if (ok === null) return;
        setOpen(null);
        setMessages((prev) => prev.filter((m) => m.uid !== uid));
        try {
          await api.deleteForever(path, [uid]);
        } catch {
          setError('Silinmədi.');
          void loadMessages(path, false);
        }
        void loadFolders();
        return;
      }

      const target = use ? findPath(use) : (dest as { path: string }).path;
      if (!target || target === path) return;
      const label = use ? null : (dest as { label: string }).label;
      setOpen(null);
      setMessages((prev) => prev.filter((m) => m.uid !== uid));
      try {
        await api.move(path, target, [uid]);
        // ⚠️ ÖZ QOVLUĞUNA KÖÇÜRMƏ TƏSDİQ TƏLƏB EDİR.
        // Sistem qovluqlarında (arxiv/spam/səbət) nəticə onsuz da
        // aydındır — sətir yoxa çıxır və geri qaytarma zolağı çıxır.
        // «Filan qovluğa» köçürəndə isə hara getdiyi görünmür.
        if (label) setNotice(`«${label}» qovluğuna köçürüldü.`);
      } catch {
        setError('Əməliyyat alınmadı.');
        void loadMessages(path, false);
      }
      void loadFolders();
    },
    [open, findPath, path, isPurgeFolder, loadMessages, loadFolders],
  );

  /**
   * Seçilmiş məktublar üzərində toplu əməliyyat.
   *
   * ⚠️ NƏTİCƏ DƏRHAL GÖSTƏRİLİR (optimistik yeniləmə). Serverdən cavab
   * gözləsək, 20 məktubu arxivləyəndə siyahı bir neçə saniyə donardı və
   * istifadəçi düyməni təkrar basardı. Xəta olsa siyahı yenidən yüklənir.
   */
  /**
   * Seçilmiş məktubları köçürür.
   *
   * ⚠️ `moveOpen` ilə eyni səbəbdən yol da qəbul edir — istifadəçinin öz
   * qovluğuna toplu köçürmə arxa tərəfdə onsuz da mümkündür.
   */
  const bulkMove = useCallback(
    async (dest: SpecialUse | { path: string; label: string }) => {
      const use = typeof dest === 'string' ? dest : null;
      if (checked.size === 0) return;
      const uids = [...checked];

      // ⚠️ SƏBƏTDƏ «SİL» KÖÇÜRMƏ OLA BİLMƏZ.
      //
      // Əvvəl bura həmişə `api.move(path, trash, …)` gedirdi. Səbətin
      // içində bu, Səbətdən Səbətə köçürmə — yəni HEÇ NƏ — deməkdir.
      // Panel sətri optimistik gizlədirdi, məktub isə qalırdı və səhifə
      // yenilənəndə geri qayıdırdı. İstifadəçi heç nəyi həmişəlik silə
      // bilmirdi; qutu yalnız böyüyürdü.
      if (use === 'trash' && isPurgeFolder) {
        if (
          (await dlg.ask({
            title: `${uids.length} məktub həmişəlik silinsin?`,
            body: 'Bu əməliyyat geri qaytarıla bilməz.',
            confirmLabel: 'Həmişəlik sil',
            danger: true,
          })) === null
        ) {
          return;
        }
        setChecked(new Set());
        setMessages((prev) => prev.filter((m) => !checked.has(m.uid)));
        try {
          await api.deleteForever(path, uids);
          setNotice(`${uids.length} məktub həmişəlik silindi.`);
        } catch {
          setError('Silinmədi.');
          void loadMessages(path, false);
        }
        void loadFolders();
        return;
      }

      const target = use ? findPath(use) : (dest as { path: string }).path;
      if (!target || target === path) return;
      setChecked(new Set());
      setMessages((prev) => prev.filter((m) => !checked.has(m.uid)));
      try {
        const r = await api.move(path, target, uids);
        setUndo({
          from: path,
          to: target,
          // ⚠️ Serverin qaytardığı YENİ UID-lər. Köhnələri işlətsək, geri
          // qaytarma səssizcə heç nə etmir — sınaqda məhz belə oldu.
          uids: r.uids.length ? r.uids : uids,
          label: `${uids.length} məktub ${
            use === 'archive'
              ? 'arxivə'
              : use === 'junk'
                ? 'spama'
                : use === 'inbox'
                  ? 'Gələnlərə'
                  : use === 'trash'
                    ? 'səbətə'
                    : `«${(dest as { label: string }).label}» qovluğuna`
          } köçürüldü`,
        });
      } catch {
        setError('Əməliyyat alınmadı.');
        void loadMessages(path, false);
      }
      void loadFolders();
    },
    [checked, findPath, path, isPurgeFolder, loadMessages, loadFolders],
  );

  const bulkSeen = useCallback(
    async (seen: boolean) => {
      if (checked.size === 0) return;
      const uids = [...checked];
      setMessages((prev) => prev.map((m) => (checked.has(m.uid) ? { ...m, seen } : m)));
      setChecked(new Set());
      try {
        await api.setFlags(path, uids, seen ? ['\\Seen'] : [], seen ? [] : ['\\Seen']);
      } catch {
        setError('Əməliyyat alınmadı.');
        void loadMessages(path, false);
      }
      void loadFolders();
    },
    [checked, path, loadMessages, loadFolders],
  );

  /**
   * Ulduzu çevirir.
   *
   * ⚠️ Optimistik: nişan dərhal dəyişir. Ulduz vurmaq ən tez-tez edilən
   * kiçik əməliyyatdır; hər dəfə serveri gözləmək interfeysi ağır göstərir.
   */
  const toggleFlag = useCallback(
    async (uid: number, flagged: boolean) => {
      setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, flagged } : m)));
      try {
        await api.setFlags(path, [uid], flagged ? ['\\Flagged'] : [], flagged ? [] : ['\\Flagged']);
      } catch {
        // Geri qaytar — server qəbul etmədi.
        setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, flagged: !flagged } : m)));
        setError('Ulduz dəyişdirilə bilmədi.');
      }
    },
    [path],
  );

  /**
   * Sətir üzərindəki sürətli əməliyyat.
   *
   * ⚠️ Optimistikdir və seçimə TOXUNMUR — istifadəçi bir neçə məktub
   * seçmiş ola bilər və başqa sətrin üzərində arxiv düyməsinə basanda
   * seçimi itirmək gözlənilməzdir.
   */
  const quickAction = useCallback(
    async (uid: number, action: 'archive' | 'trash' | 'seen') => {
      if (action === 'seen') {
        const cur = messages.find((m) => m.uid === uid);
        const next = !(cur?.seen ?? false);
        setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: next } : m)));
        try {
          await api.setFlags(path, [uid], next ? ['\\Seen'] : [], next ? [] : ['\\Seen']);
        } catch {
          setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: !next } : m)));
        }
        void loadFolders();
        return;
      }

      // Səbətdə «Sil» həmişəlikdir — bulk əməliyyatla eyni qayda.
      if (action === 'trash' && isPurgeFolder) {
        if (
          (await dlg.ask({
            title: 'Məktub həmişəlik silinsin?',
            body: 'Geri qaytarmaq mümkün olmayacaq.',
            confirmLabel: 'Həmişəlik sil',
            danger: true,
          })) === null
        ) {
          return;
        }
        setMessages((prev) => prev.filter((m) => m.uid !== uid));
        await api.deleteForever(path, [uid]).catch(() => setError('Silinmədi.'));
        void loadFolders();
        return;
      }

      const target = findPath(action === 'archive' ? 'archive' : 'trash');
      if (!target) return;
      setMessages((prev) => prev.filter((m) => m.uid !== uid));
      try {
        const r = await api.move(path, target, [uid]);
        setUndo({
          from: path,
          to: target,
          uids: r.uids.length ? r.uids : [uid],
          label: action === 'archive' ? 'Arxivə köçürüldü' : 'Səbətə köçürüldü',
        });
      } catch {
        setError('Əməliyyat alınmadı.');
        void loadMessages(path, false);
      }
      void loadFolders();
    },
    [messages, path, isPurgeFolder, findPath, loadMessages, loadFolders],
  );

  /** Hamısını seç / seçimi ləğv et. */
  const toggleAll = useCallback(() => {
    setChecked((prev) => (prev.size === messages.length ? new Set() : new Set(messages.map((m) => m.uid))));
  }, [messages]);

  const toggleCheck = useCallback((uid: number, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(uid);
      else next.delete(uid);
      return next;
    });
  }, []);

  if (section !== 'mail') {
    return (
      // ⚠️ `is-wide` MƏCBURİDİR. `.shell` üç sütunlu griddir
      // (rail · qovluqlar · məzmun). Bu ekranlarda qovluq siyahısı yoxdur,
      // ona görə məzmun 240px-lik sütuna düşür və cədvəllər kəsilir.
      <div className="shell is-wide">
        <AppRail user={user} active={section} onSelect={setSection} onLogout={onLogout} />
        {dlg.node}
        <main className="content">
          {section === 'migration' ? (
            <Migration onClose={() => setSection('mail')} />
          ) : section === 'admin' ? (
            <Admin user={user} />
          ) : section === 'billing' ? (
            <Billing />
          ) : section === 'settings' ? (
            <Settings
              user={user}
              onChanged={onProfileChanged}
              onReauth={onLogout}
              tabsOn={tabsOn}
              onToggleTabs={(on) => {
                setTabsOn(on);
                localStorage.setItem('mailora.tabs', on ? 'on' : 'off');
                // ⚠️ SÖNDÜRÜLƏNDƏ SEÇİLMİŞ NİŞAN DA TƏMİZLƏNİR.
                // Qalsaydı, istifadəçi nişanları yenidən açanda özünün
                // seçmədiyi «Tanıtımlar» siyahısında oyanardı.
                if (!on) {
                  setCategory(null);
                  localStorage.removeItem('mailora.category');
                }
                void loadMessages(path, false, on ? category : null);
              }}
            />
          ) : (
            <div className="placeholder">
              <h2>Bu bölmə hazırlanır</h2>
              <button className="btn-text" onClick={() => setSection('mail')}>
                Poçta qayıt
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className={`shell${drawer ? ' drawer-open' : ''}`}>
      <AppRail user={user} active={section} onSelect={setSection} onLogout={onLogout} />
      {/* ⚠️ HƏR İKİ RENDER QOLUNDA OLMALIDIR — yoxsa digər bölmələrdə
          dialoq açılır, amma heç nə görünmür. */}
      {dlg.node}

      {/*
        HESAB VƏZİYYƏTİ — POÇT EKRANININ BAŞINDA, QALICI.
        ⚠️ `.shell` griddir; zolaq bütün sütunları keçməlidir, yoxsa
        yalnız qovluq sütununun üstündə görünüb sıxılardı. CSS-də
        `grid-column: 1 / -1` ilə həll olunur.
      */}
      <AccountBanner user={user} onGoBilling={() => setSection('billing')} />

      {/* Siyirmə açıqkən arxa fon — ona toxunmaq bağlayır. */}
      {drawer && (
        <button
          className="drawer-scrim"
          aria-label="Qovluq siyahısını bağla"
          onClick={() => setDrawer(false)}
        />
      )}

      <FolderList
        folders={folders}
        selected={path}
        onSelect={setPath}
        onCompose={() => setDraft({})}
        onRefresh={() => void refresh()}
        refreshing={refreshing}
        quota={quota}
        onFoldersChanged={() => {
          void loadFolders();
          void api.quota().then((r) => setQuota(r.quota)).catch(() => undefined);
        }}
        onError={setError}
      />

      <main className="content">
      {/*
        MOBİL BAŞLIQ — yalnız dar ekranda görünür (CSS).
        Cari qovluğun adını göstərir və siyirməni açır. Onsuz telefonda
        qovluq dəyişmək mümkün deyil.
      */}
      <div className="mobile-head">
        <button
          className="btn-icon"
          onClick={() => setDrawer(true)}
          aria-label="Qovluqlar"
          aria-expanded={drawer}
        >
          <Icon name="menu" size={20} />
        </button>
        {/* ⚠️ `folderLabel` — xam `name` DEYİL. Xam ad IMAP-dan gəlir
            («INBOX», «Sent»); yan paneldə isə «Gələnlər» yazılır. Eyni
            qovluğun iki fərqli adı istifadəçini çaşdırır. */}
        <span className="mobile-folder">
          {(() => {
            const f = folders.find((x) => x.path === path);
            return f ? folderLabel(f) : path;
          })()}
        </span>
        <button
          className="btn-icon"
          onClick={() => void refresh()}
          aria-label="Yenilə"
        >
          <span className={refreshing ? 'spin' : ''}>
            <Icon name="refresh" size={18} />
          </span>
        </button>
      </div>
        <div className="search-bar">
          <input
            type="search"
            value={query}
            placeholder="Məktublarda axtar — göndərən, mövzu və ya mətn"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void runSearch(query);
              if (e.key === 'Escape') {
                setQuery('');
                void runSearch('');
              }
            }}
          />
          <button
            className="btn-text"
            disabled={searching || query.trim().length < 2}
            onClick={() => void runSearch(query)}
          >
            {searching ? 'Axtarılır…' : 'Axtar'}
          </button>
          {searchInfo ? (
            <>
              <span className="search-info">{searchInfo}</span>
              <button
                className="btn-text"
                onClick={() => {
                  setQuery('');
                  void runSearch('');
                }}
              >
                Təmizlə
              </button>
            </>
          ) : null}
        </div>

        {/*
          ⚠️ MƏTN SƏBƏB İDDİA ETMİR, ƏLAMƏTİ TƏSVİR EDİR.
          Əvvəl burada «server provayderi 25-ci portu hələ açmayıb» yazılırdı.
          Port açılandan sonra bu cümlə YALANA çevrildi və istifadəçi mövcud
          olmayan problemi gözləyirdi. Eyni əlamətin başqa səbəbləri də var
          (alıcının serveri cavab vermir, şəbəkə nasazlığı) — interfeys
          gördüyünü deməlidir, təxmin etdiyini yox.
        */}
        <VerifyBanner kind={user.kind} emailVerified={user.emailVerified} />

        {outboundBlocked ? (
          <div className="banner is-warn" role="status">
            <strong>Kənar ünvanlara göndəriş gecikir.</strong> Şirkət
            daxilindəki ünvanlara məktublar normal çatır. Kənar ünvanlara
            (Gmail, Outlook və s.) göndərilənlər növbədə gözləyir və server
            avtomatik təkrar cəhd edir. Məktublar itmir.
          </div>
        ) : null}
        {(error ?? notice) && (
          <div className={notice && !error ? 'banner is-ok' : 'banner'} role="status">
            {error ?? notice}
            <button
              className="btn-icon"
              onClick={() => {
                setError(null);
                setNotice(null);
              }}
              aria-label="Bağla"
            >
              <Icon name="close" size={17} />
            </button>
          </div>
        )}

        {/*
          SEÇİM PANELİ — qutucuqlar yalnız seçir, əməliyyat isə burada olur.
          ⚠️ Bu panel olmadan qutucuqlar SINIQ görünürdü: istifadəçi bir neçə
          məktub seçirdi və heç nə baş vermirdi. Seçim mexanizmi olub,
          onunla nə edəcəyini göstərməmək interfeysdə ən pis haldır.
        */}
        {/* «Səbəti boşalt» — yalnız Səbət/Spamda və yalnız məktub varsa. */}
        {!open && isPurgeFolder && checked.size === 0 && messages.length > 0 && (
          <div className="purgebar">
            <span className="purge-text">
              Buradakı məktublar yer tutmağa davam edir.
            </span>
            <button
              className="btn-text btn-danger"
              onClick={() => {
                void (async () => {
                  const ok = await dlg.ask({
                    title: `«${folders.find((f) => f.path === path)?.name ?? path}» boşaldılsın?`,
                    body: `${total} məktub HƏMİŞƏLİK silinəcək. Geri qaytarmaq mümkün olmayacaq.`,
                    confirmLabel: 'Boşalt',
                    danger: true,
                  });
                  if (ok === null) return;
                  try {
                    const r = await api.emptyFolder(path);
                    setMessages([]);
                    setTotal(0);
                    setNotice(`${r.deleted} məktub həmişəlik silindi.`);
                  } catch {
                    setError('Qovluq boşaldıla bilmədi.');
                  }
                  void loadFolders();
                })();
              }}
            >
              Qovluğu boşalt
            </button>
          </div>
        )}

        {/* ⚠️ Geri qaytarma zolağı DİQQƏT ÇƏKMƏLİDİR, amma yol kəsməməlidir:
            12 saniyə sonra özü yox olur və heç bir klik tələb etmir. */}
        {undo && (
          <div className="undobar" role="status">
            <span>{undo.label}</span>
            <button className="btn-text" onClick={() => void runUndo()}>
              Geri qaytar
            </button>
            <button
              className="btn-icon"
              onClick={() => setUndo(null)}
              aria-label="Bağla"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        )}

        {!open && checked.size > 0 && (
          <div className="bulkbar" role="toolbar" aria-label="Seçilmiş məktublar">
            <button className="btn-icon" onClick={() => setChecked(new Set())} aria-label="Seçimi ləğv et">
              <Icon name="close" size={17} />
            </button>
            <strong className="bulk-count">{checked.size} seçildi</strong>
            <span className="bulk-sep" />
            <button className="btn-text" onClick={() => void bulkSeen(true)}>
              Oxunmuş kimi
            </button>
            <button className="btn-text" onClick={() => void bulkSeen(false)}>
              Oxunmamış kimi
            </button>
            {/*
              ⚠️ SƏBƏT VƏ SPAMDA «BƏRPA ET» GÖSTƏRİLİR.
              Bu qovluqlarda ən çox lazım olan hərəkət geri qaytarmaqdır;
              əvvəl yalnız «Spam» və «Sil» vardı, yəni səhvən silinmiş
              məktubu toplu şəkildə qaytarmağın heç bir yolu yox idi.
            */}
            {isPurgeFolder && (
              <button className="btn-text is-primary" onClick={() => void bulkMove('inbox')}>
                {currentUse === 'junk' ? 'Spam deyil' : 'Bərpa et'}
              </button>
            )}
            {!isPurgeFolder && (
              <button className="btn-text" onClick={() => void bulkMove('archive')}>
                Arxivə
              </button>
            )}
            {currentUse !== 'junk' && (
              <button className="btn-text" onClick={() => void bulkMove('junk')}>
                Spam
              </button>
            )}

            {/* Öz qovluqlarına toplu köçürmə — arxa tərəf onsuz da dəstəkləyir. */}
            {folders.some((f) => !f.specialUse && f.path !== path) && (
              <details className="reader-move">
                <summary className="btn-text">Qovluğa köçür</summary>
                <ul className="reader-move-menu">
                  {folders
                    .filter((f) => !f.specialUse && f.path !== path)
                    .map((f) => (
                      <li key={f.path}>
                        <button
                          type="button"
                          onClick={() => void bulkMove({ path: f.path, label: f.name })}
                        >
                          {f.name}
                        </button>
                      </li>
                    ))}
                </ul>
              </details>
            )}
            {/* ⚠️ AD ƏMƏLİYYATI DEYİR. Səbətdə «Sil» geri dönüşü olmayan
                əməliyyatdır — eyni sözlə adlandırmaq istifadəçini aldadar. */}
            <button className="btn-text btn-danger" onClick={() => void bulkMove('trash')}>
              {isPurgeFolder ? 'Həmişəlik sil' : 'Sil'}
            </button>
          </div>
        )}

        {open ? (
          <MessageView
            message={open}
            path={path}
            onReply={() => setDraft(replyTo(open))}
            onReplyAll={
              // Düymə yalnız BAŞQA alıcı varsa göstərilir — tək adama
              // yazılmış məktubda «hamısına cavab» məna daşımır.
              user.mailboxEmail &&
              [...open.to, ...open.cc].some(
                (a) =>
                  a.address.toLowerCase() !== user.mailboxEmail?.toLowerCase() &&
                  a.address.toLowerCase() !== (open.from?.address ?? '').toLowerCase(),
              )
                ? () => setDraft(replyAllTo(open, user.mailboxEmail ?? ''))
                : null
            }
            onForward={() => setDraft(forwardOf(open, path))}
            onBack={() => setOpen(null)}
            onArchive={() => void moveOpen('archive')}
            onSpam={() => void moveOpen('junk')}
            onDelete={() => void moveOpen('trash')}
            inJunk={currentUse === 'junk'}
            inTrash={currentUse === 'trash'}
            // Bərpa = Gələnlərə qaytarmaq. Həm Səbətdə, həm Spamda eyni hərəkət.
            onRestore={() => void moveOpen('inbox')}
            onMarkUnread={() => {
              const uid = open.uid;
              // ⚠️ OXUCU BAĞLANIR. Açıq qalsa, `getMessage` onu yenidən
              // oxunmuş edərdi — istifadəçi düymənin işləmədiyini düşünərdi.
              setOpen(null);
              setMessages((prev) =>
                prev.map((m) => (m.uid === uid ? { ...m, seen: false } : m)),
              );
              void api
                .setFlags(path, [uid], [], ['\\Seen'])
                .then(() => loadFolders())
                .catch(() => setError('Oxunmamış işarələnmədi.'));
            }}
            customFolders={folders
              .filter((f) => !f.specialUse && f.path !== path)
              .map((f) => ({ path: f.path, label: f.name }))}
            onMoveTo={(dest) => void moveOpen(dest)}
          />
        ) : (
          <>
            {/*
              NİŞANLAR — YALNIZ GƏLƏNLƏR QUTUSUNDA VƏ YALNIZ AXTARIŞDAN KƏNARDA.
              «Göndərilənlər»də hamısı bizim yazdığımızdır; axtarış isə
              onsuz da bütün qutunu gəzir və nişanla məhdudlaşdırmaq
              nəticəni səssizcə azaldardı.

              ⚠️ SEÇİM DƏYİŞƏNDƏ SİYAHI YENİDƏN SERVERDƏN GƏLİR, ön
              tərəfdə süzülmür. Səbəb: «İlkin» nişanında son 50 məktubun
              cəmi 3-ü ola bilər — süzgəci burada etsək, ekranda 3 sətir
              görünərdi və qalanı üçün dəfələrlə «daha çox» basmaq
              lazım gələrdi.
            */}
            {tabsOn && currentUse === 'inbox' && query.trim().length < 2 && (
              <CategoryTabs
                active={category}
                counts={counts}
                scanned={scanned}
                total={total}
                busy={loadingList}
                onSelect={(c) => {
                  setCategory(c);
                  if (c) localStorage.setItem('mailora.category', c);
                  else localStorage.removeItem('mailora.category');
                  setChecked(new Set());
                  void loadMessages(path, true, c);
                }}
              />
            )}
            <MessageList
            messages={unreadOnly ? messages.filter((m) => !m.seen) : messages}
            selectedUid={null}
            checked={checked}
            onOpen={(uid) => void openMessage(uid)}
            onCheck={toggleCheck}
            onToggleAll={toggleAll}
            onToggleFlag={(uid, f) => void toggleFlag(uid, f)}
            onQuick={(uid, a) => void quickAction(uid, a)}
            unreadOnly={unreadOnly}
            onToggleUnread={() => setUnreadOnly((v) => !v)}
            // Göndərilənlər və Qaralamalarda göndərən deyil, ALICI göstərilir.
            showRecipient={currentUse === 'sent' || currentUse === 'drafts'}
            total={query.trim().length >= 2 ? messages.length : total}
            loadingMore={loadingMore}
            onLoadMore={() => void loadMore()}
            loading={loadingList}
              hasError={listError}
              onRetry={() => void loadMessages(path, true)}
            />
          </>
        )}
      </main>

      {draft && user.mailboxEmail ? (
        <Compose
          from={user.mailboxEmail}
          draft={draft}
          onClose={() => setDraft(null)}
          onSent={(r) => {
            setDraft(null);
            // Qismən uğur mümkündür — bəzi alıcılar rədd edilə bilər.
            setNotice(
              r.rejected.length
                ? `Göndərildi, amma ${r.rejected.length} ünvan rədd edildi: ${r.rejected.join(', ')}`
                : r.savedToSent
                  ? 'Məktub göndərildi.'
                  : 'Məktub göndərildi, amma Göndərilənlər qovluğuna yazıla bilmədi.',
            );
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
